import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Flex, HStack, Text, Heading, Icon, Button, Skeleton, VStack, Input,
  InputGroup, InputLeftElement, Select, Tooltip, IconButton,
} from "@chakra-ui/react";
import {
  HiOutlineArrowLeft, HiOutlineMagnifyingGlass, HiOutlineClipboardDocumentList,
  HiOutlineArrowTopRightOnSquare, HiOutlineChartBar, HiOutlineUsers,
} from "react-icons/hi2";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_API_URL } from "../../Components/config";
import { buildMatcher, haystack } from "../../Components/searchMatch";
import { c } from "../classroomColors";
import { brand, onTint } from "../standeTokens";
import { StateLegend, StateColumns, MarkDistribution } from "./Charts";
import {
  groupByAssignment, stageOf, STAGES, STATE_ORDER, studentName, formatDate,
  formatDateTime, bandCounts, pctOf, chartColors,
} from "./markingTokens";

/** A headline number. Not a one-bar chart — just the figure and its label. */
const Tile = ({ label, value, hint, tone }) => (
  <Box flex="1 1 140px" minW="140px">
    <Text fontSize="12px" color={c.textMuted} mb="5px">
      {label}
    </Text>
    <HStack spacing="6px" align="baseline">
      <Text fontSize="26px" fontWeight="500" lineHeight="1" color={tone || c.text}
        sx={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Text>
      {hint && (
        <Text fontSize="12.5px" fontWeight="300" color={c.textMuted}>
          {hint}
        </Text>
      )}
    </HStack>
  </Box>
);

const Card = ({ title, meta, children, ...rest }) => (
  <Box bg={c.surface} border="1px solid" borderColor={c.borderSoft} borderRadius="14px" p="18px" {...rest}>
    {(title || meta) && (
      <Flex justify="space-between" align="baseline" gap="12px" mb="14px" wrap="wrap">
        {title && (
          <Text fontSize="14px" fontWeight="600" color={c.text}>
            {title}
          </Text>
        )}
        {meta}
      </Flex>
    )}
    {children}
  </Box>
);

const Empty = ({ title, body }) => (
  <Flex direction="column" align="center" justify="center" py="60px" gap="9px" textAlign="center">
    <Flex boxSize="44px" borderRadius="full" bg={c.surfaceSubtle} align="center" justify="center">
      <Icon as={HiOutlineClipboardDocumentList} boxSize="19px" color={c.textMuted} />
    </Flex>
    <Text fontSize="15px" fontWeight="500" color={c.text}>{title}</Text>
    {body && (
      <Text fontSize="13px" fontWeight="300" color={c.textSub} maxW="340px">{body}</Text>
    )}
  </Flex>
);

/* ------------------------------------------------------------------ */

/**
 * Marking, inside the classroom and organised by assignment.
 *
 * The overview answers "what needs me?" without scrolling; opening one
 * assignment answers "how did the class do, and who still needs marking?".
 */
const MarkingPanel = ({ courseGuid, isInstructor, isDark }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [query, setQuery] = useState("");
  const [range, setRange] = useState("all");

  const b = brand(isDark);
  const tint = onTint(isDark);
  const cc = chartColors(isDark);
  const token = `Bearer ${localStorage.getItem("accessToken")}`;

  useEffect(() => {
    if (!courseGuid) return;
    let cancelled = false;
    axios
      .get(
        `${BASE_API_URL}/api/v1/user-course-links/user-paper-attempts/detail/?course_user_type=${
          isInstructor ? "INSTRUCTOR" : "STUDENT"
        }&course_guid=${courseGuid}&limit=200`,
        { headers: { Authorization: token } }
      )
      .then((res) => { if (!cancelled) { setRows(res.data || []); setError(null); } })
      .catch((e) => {
        if (cancelled) return;
        console.error(e);
        setRows([]);
        setError("Could not load the work for this space.");
      });
    return () => { cancelled = true; };
  }, [courseGuid, isInstructor, token]);

  // Date filter applies to when work was last touched, which is what a teacher
  // means by "this week".
  const withinRange = useMemo(() => {
    if (range === "all") return () => true;
    const days = range === "7" ? 7 : 30;
    const cutoff = Date.now() - days * 86400000;
    return (r) => {
      const t = r.updated_date_time ? new Date(r.updated_date_time).getTime() : null;
      return t == null || t >= cutoff;
    };
  }, [range]);

  const assignments = useMemo(
    () => groupByAssignment((rows || []).filter(withinRange)),
    [rows, withinRange]
  );

  const open = assignments.find((a) => a.key === openKey) || null;

  const totals = useMemo(() => {
    const counts = { not_submitted: 0, to_review: 0, marked: 0, released: 0 };
    (rows || []).filter(withinRange).forEach((r) => { counts[stageOf(r)] += 1; });
    const students = new Set((rows || []).map((r) => r.user_guid)).size;
    return { counts, students };
  }, [rows, withinRange]);

  const review = async (row) => {
    try {
      const res = await axios.get(
        `${BASE_API_URL}/api/v1/user-paper-attempts/questions-with-attempt/${row.guid}`,
        { headers: { Authorization: token } }
      );
      navigate("/my-papers/solve-paper", {
        state: {
          questions: res.data, paper_Guid: row.guid, subject_name: row.subject_name,
          formattedPaperName: row.paper_name, time_hour: 0, time_min: 0,
          paperMode: "REVIEW", viewerRole: "INSTRUCTOR",
          feedbackReleased: row.feedback_released,
        },
      });
    } catch (e) {
      console.error(e);
      alert("Could not load paper for review.");
    }
  };

  const report = (row) =>
    navigate(`/classroom/report/${row.guid}`, {
      state: { viewerRole: isInstructor ? "INSTRUCTOR" : "STUDENT" },
    });

  if (rows === null) {
    return (
      <VStack align="stretch" spacing="14px">
        <Skeleton h="92px" borderRadius="14px" startColor={c.surfaceSubtle} endColor={c.hover} />
        <Skeleton h="230px" borderRadius="14px" startColor={c.surfaceSubtle} endColor={c.hover} />
      </VStack>
    );
  }

  if (error) return <Empty title="Couldn't load this" body={error} />;
  if (!assignments.length && range === "all") {
    return (
      <Empty
        title="No work set yet"
        body={isInstructor
          ? "Set an assignment from a session's post-class tab and it will show up here."
          : "Your teacher hasn't set any work in this space yet."}
      />
    );
  }

  /* ---------------- one assignment ---------------- */
  if (open) {
    const matches = buildMatcher(query);
    const students = open.rows
      .filter((r) => matches(haystack([studentName(r)])))
      .sort((x, y) => {
        // Work that needs marking first, then by name.
        const rank = (r) => (stageOf(r) === "to_review" ? 0 : stageOf(r) === "not_submitted" ? 2 : 1);
        return rank(x) - rank(y) || studentName(x).localeCompare(studentName(y));
      });

    return (
      <VStack align="stretch" spacing="14px">
        <Flex align="center" gap="12px" wrap="wrap">
          <IconButton
            aria-label="Back to all assignments"
            icon={<Icon as={HiOutlineArrowLeft} boxSize="16px" />}
            size="sm" h="32px" minW="32px" borderRadius="8px" variant="outline"
            borderColor={c.border} color={c.textSub}
            _hover={{ bg: c.hover, color: c.text }}
            onClick={() => { setOpenKey(null); setQuery(""); }}
          />
          <Box minW={0} flex="1">
            <Text fontSize="11.5px" color={c.textMuted} letterSpacing="0.03em">
              {String(open.kind || "ASSIGNMENT").toUpperCase()}
              {open.dueDate ? ` · DUE ${formatDate(open.dueDate).toUpperCase()}` : ""}
            </Text>
            <Heading fontSize="21px" fontWeight="500" color={c.text} letterSpacing="-0.01em" noOfLines={1}>
              {open.name}
            </Heading>
          </Box>
        </Flex>

        <Flex gap="14px" direction={{ base: "column", lg: "row" }} align="flex-start">
          <Card flex="1 1 320px">
            <Flex gap="18px" wrap="wrap" mb="16px">
              <Tile label="Handed in" value={open.submitted} hint={`of ${open.total}`} />
              <Tile label="To review" value={open.needsReview}
                tone={open.needsReview ? b.secondaryOrange : c.text} />
              <Tile label="Average"
                value={open.average == null ? "—" : `${Math.round(open.average)}%`} />
            </Flex>
            {/* This assignment's own part-to-whole, so the card carries the
                same shape the overview row did rather than dead space. */}
            <Flex height="12px" gap="2px" borderRadius="6px" overflow="hidden" bg={c.track} mb="14px">
              {STATE_ORDER.map((k) => open.counts[k]).some(Boolean) &&
                STATE_ORDER.filter((k) => open.counts[k] > 0).map((k, i, arr) => (
                  <Box
                    key={k}
                    flex={`${open.counts[k]} 0 0`}
                    bg={cc[k]}
                    borderLeftRadius={i === 0 ? "6px" : "0"}
                    borderRightRadius={i === arr.length - 1 ? "6px" : "0"}
                    title={`${open.counts[k]} ${STAGES[k].label.toLowerCase()}`}
                  />
                ))}
            </Flex>
            <StateLegend isDark={isDark} counts={open.counts} />
          </Card>

          <Card title="Spread of marks" flex="1 1 380px">
            <MarkDistribution counts={bandCounts(open.rows)} isDark={isDark} average={open.average} />
          </Card>
        </Flex>

        <Card
          title="Students"
          meta={
            <InputGroup size="sm" maxW="220px">
              <InputLeftElement pointerEvents="none" h="32px">
                <Icon as={HiOutlineMagnifyingGlass} color={c.textMuted} boxSize="14px" />
              </InputLeftElement>
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Find a student" h="32px" borderRadius="8px" bg={c.inputBg}
                border="1px solid" borderColor={c.inputBorder} color={c.text} fontSize="12.5px"
                _placeholder={{ color: c.textMuted }}
                _focus={{ borderColor: c.textMuted, boxShadow: "none" }} />
            </InputGroup>
          }
        >
          {students.length === 0 ? (
            <Text fontSize="13px" color={c.textMuted} py="20px" textAlign="center">
              No students match that.
            </Text>
          ) : (
            <VStack align="stretch" spacing="1px">
              {students.map((r) => {
                const stage = stageOf(r);
                const pct = pctOf(r);
                return (
                  <Flex key={r.guid} align="center" gap="13px" px="10px" py="10px"
                    borderRadius="9px" transition="background 0.15s" _hover={{ bg: c.hover }}>
                    <Box boxSize="8px" borderRadius="2px" bg={cc[stage]} flexShrink={0} />
                    <Box flex="1 1 auto" minW={0}>
                      <Text fontSize="13.5px" fontWeight="500" color={c.text} isTruncated>
                        {studentName(r)}
                      </Text>
                      <Text fontSize="11.5px" color={c.textMuted} isTruncated>
                        {STAGES[stage].label}
                        {r.submission_status === "SUBMITTED" && r.updated_date_time
                          ? ` · ${formatDateTime(r.updated_date_time)}`
                          : ""}
                      </Text>
                    </Box>
                    <Text flex="0 0 74px" textAlign="right" fontSize="14px"
                      fontWeight={pct == null ? "300" : "600"}
                      color={pct == null ? c.textMuted : c.text}
                      sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {pct == null ? "—" : `${r.total_scored_marks}/${r.total_marks}`}
                    </Text>
                    <HStack spacing="5px" flex="0 0 auto">
                      {r.submission_status === "SUBMITTED" && (
                        <>
                          <Tooltip label="Review submission" placement="top" hasArrow fontSize="11px">
                            <IconButton aria-label="Review submission"
                              icon={<Icon as={HiOutlineArrowTopRightOnSquare} boxSize="14px" />}
                              size="sm" minW="30px" h="30px" borderRadius="7px" variant="outline"
                              borderColor={c.border} color={c.textSub}
                              _hover={{ bg: c.hover, color: c.text, borderColor: c.textMuted }}
                              onClick={() => review(r)} />
                          </Tooltip>
                          <Tooltip label="Open report" placement="top" hasArrow fontSize="11px">
                            <IconButton aria-label="Open report"
                              icon={<Icon as={HiOutlineChartBar} boxSize="14px" />}
                              size="sm" minW="30px" h="30px" borderRadius="7px" variant="outline"
                              borderColor={c.border} color={c.textSub}
                              _hover={{ bg: c.hover, color: c.text, borderColor: c.textMuted }}
                              onClick={() => report(r)} />
                          </Tooltip>
                        </>
                      )}
                    </HStack>
                  </Flex>
                );
              })}
            </VStack>
          )}
        </Card>
      </VStack>
    );
  }

  /* ---------------- the overview ---------------- */
  return (
    <VStack align="stretch" spacing="14px">
      <Card>
        <Flex justify="space-between" align="flex-start" gap="14px" wrap="wrap" mb="16px">
          <Flex gap="20px" wrap="wrap" flex="1">
            <Tile label="Students" value={totals.students} />
            <Tile label="Assignments" value={assignments.length} />
            <Tile label="To review" value={totals.counts.to_review}
              tone={totals.counts.to_review ? b.secondaryOrange : c.text} />
            <Tile label="Released" value={totals.counts.released} />
          </Flex>
          <Select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            size="sm" width="auto" h="32px" borderRadius="8px" bg={c.inputBg}
            borderColor={c.inputBorder} color={c.textSub} fontSize="12.5px"
            _focus={{ borderColor: c.textMuted, boxShadow: "none" }}
          >
            <option value="all">All time</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
          </Select>
        </Flex>
      </Card>

      <Card
        title="Where each assignment stands"
        meta={
          <Text fontSize="11.5px" color={c.textMuted}>
            Most in need of marking, highlighted
          </Text>
        }
      >
        {assignments.length === 0 ? (
          <Text fontSize="13px" color={c.textMuted} py="24px" textAlign="center">
            Nothing in this period.
          </Text>
        ) : (
          <>
            <StateColumns items={assignments} isDark={isDark}
              onSelect={(a) => setOpenKey(a.key)} />
            <Box mt="16px" pt="14px" borderTop="1px solid" borderColor={c.borderSoft}>
              <StateLegend isDark={isDark} counts={totals.counts} />
            </Box>
          </>
        )}
      </Card>

      {assignments.some((a) => a.needsReview > 0) && (
        <Flex
          align="center" gap="10px" px="15px" py="12px" borderRadius="12px"
          bg={b.lightYellow} border="1px solid" borderColor="transparent"
        >
          <Icon as={HiOutlineUsers} boxSize="16px" color={tint.lightYellow} flexShrink={0} />
          <Text fontSize="13px" color={tint.lightYellow} flex="1">
            {totals.counts.to_review} submission{totals.counts.to_review === 1 ? "" : "s"} waiting on you.
          </Text>
          <Button
            size="xs" h="28px" px="11px" borderRadius="7px"
            bg={c.solidBg} color={c.solidText} fontSize="12px" fontWeight="500"
            _hover={{ opacity: 0.88 }}
            onClick={() => setOpenKey(assignments.find((a) => a.needsReview > 0).key)}
          >
            Start marking
          </Button>
        </Flex>
      )}
    </VStack>
  );
};

export default MarkingPanel;
